import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, Loader2, Plus, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useRecommendQuestions } from "@/hooks/genie-documents/useRecommendQuestions";
import type { HubFeaturedQuestion } from "@/types/agent/info-links";
import type { FeaturedQuestionSuggestion, FeaturedQuestionsPickerProps } from "@/types/agent/featured-questions-picker";
import type { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";

/** Questions on the page. Past this it stops being a shortlist. */
const MAX_QUESTIONS = 16;

const normalise = (question: string) => question.trim().toLowerCase().replace(/\s+/g, " ");

const getAnswers = (document: DocumentWithMetadata): string[] => {
  const raw = document.metadata?.answers;
  return Array.isArray(raw) ? raw.filter((value): value is string => typeof value === "string") : [];
};

const getTopic = (document: DocumentWithMetadata): string | null => {
  const raw = document.metadata?.topics;
  return Array.isArray(raw) && typeof raw[0] === "string" ? raw[0] : null;
};

/**
 * Picks which questions lead a Genie Page.
 *
 * Fresh recommendations are shown directly from the mutation response before
 * past caller questions and source metadata. Persisted metadata supplies them
 * on subsequent visits; rendering never depends on that refetch completing.
 * Nothing is selected automatically — the owner chooses what to feature.
 *
 * Questions that are only ever *asked* (with no guide behind them) are still
 * worth featuring: the Genie may answer them from its prompt or another source,
 * and if it can't, that is exactly the gap the owner should see.
 */
export const FeaturedQuestionsPicker = ({
  value,
  onChange,
  documents,
  askedQuestions,
  genieId,
}: FeaturedQuestionsPickerProps) => {
  const [draft, setDraft] = useState("");
  const [showAllSuggestions, setShowAllSuggestions] = useState(false);
  const recommendQuestions = useRecommendQuestions();
  const recommendedQuestions = recommendQuestions.variables === genieId
    ? recommendQuestions.data?.questions
    : undefined;

  // Only knowledge sources feed the recommender — never a generated guide.
  const sourceCount = useMemo(
    () => documents.filter((doc) => doc.source_type !== "genie-document").length,
    [documents],
  );

  const handleRecommend = async () => {
    try {
      const result = await recommendQuestions.mutateAsync(genieId);
      setShowAllSuggestions(false);
      if (result.questions.length === 0) {
        toast.info("No questions could be recommended from these sources.");
        return;
      }
      if (result.questions.every((question) => value.some((entry) => normalise(entry.question) === normalise(question)))) {
        toast.info("These recommended questions are already on your page.");
        return;
      }
      toast.success(
        `Recommended ${result.questions.length} question${result.questions.length === 1 ? "" : "s"} — pick from the top of Suggestions to add them to your page.`,
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Couldn't recommend questions");
    }
  };

  const chosen = useMemo(() => new Set(value.map((entry) => normalise(entry.question))), [value]);

  const suggestions = useMemo<FeaturedQuestionSuggestion[]>(() => {
    const seen = new Set<string>();
    const list: FeaturedQuestionSuggestion[] = [];

    // Use the response immediately, even before source metadata refreshes.
    // Scope it to this Genie and keep source links when metadata is available.
    for (const question of recommendedQuestions ?? []) {
      const key = normalise(question);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      const source = documents.find((document) => getAnswers(document).some((answer) => normalise(answer) === key));
      list.push({
        question,
        documentId: source?.id ?? null,
        topic: source ? getTopic(source) : null,
        occurrences: null,
        sourceName: source?.name ?? null,
        recommended: true,
      });
    }

    // Existing caller questions follow the fresh recommendations.
    for (const asked of askedQuestions) {
      const key = normalise(asked.question);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      list.push({
        question: asked.question,
        documentId: null,
        topic: null,
        occurrences: asked.occurrences,
        sourceName: null,
      });
    }

    for (const document of documents) {
      const topic = getTopic(document);
      for (const answer of getAnswers(document)) {
        const key = normalise(answer);
        if (!key || seen.has(key)) continue;
        seen.add(key);
        list.push({
          question: answer,
          documentId: document.id,
          topic,
          occurrences: null,
          sourceName: document.name,
        });
      }
    }

    return list.filter((suggestion) => !chosen.has(normalise(suggestion.question)));
  }, [documents, askedQuestions, chosen, recommendedQuestions]);

  // Never hide a fresh recommendation behind the initial suggestion limit.
  const initialSuggestionCount = Math.max(24, suggestions.filter((suggestion) => suggestion.recommended).length);
  const visibleSuggestions = showAllSuggestions ? suggestions : suggestions.slice(0, initialSuggestionCount);

  const atCapacity = value.length >= MAX_QUESTIONS;

  const add = (entry: Omit<HubFeaturedQuestion, "id">) => {
    if (atCapacity) return;
    const key = normalise(entry.question);
    if (!key || chosen.has(key)) return;
    onChange([...value, { ...entry, id: `q-${key.slice(0, 40)}-${value.length}` }]);
  };

  const remove = (id: string) => onChange(value.filter((entry) => entry.id !== id));

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= value.length) return;
    const next = [...value];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const addDraft = () => {
    const question = draft.trim();
    if (!question) return;
    add({ question, documentId: null, topic: null });
    setDraft("");
  };

  return (
    <div className="space-y-5">
      <div>
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">On the page</span>
          <span className="text-xs text-gray-400">
            {value.length}/{MAX_QUESTIONS}
          </span>
        </div>

        {value.length === 0 ? (
          <p className="rounded-xl bg-gray-50 px-4 py-3 text-[13px] leading-snug text-gray-600">
            Nothing picked yet — so the page shows every question your published guides say they
            answer. Pick some here to lead with the ones that matter most.
          </p>
        ) : (
          <ul className="space-y-2">
            {value.map((entry, index) => (
              <li
                key={entry.id}
                className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2.5"
              >
                <span className="min-w-0 flex-1 text-sm text-gray-800">{entry.question}</span>
                <button
                  type="button"
                  onClick={() => move(index, -1)}
                  disabled={index === 0}
                  aria-label="Move up"
                  className="rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-700 disabled:opacity-30"
                >
                  <ArrowUp className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => move(index, 1)}
                  disabled={index === value.length - 1}
                  aria-label="Move down"
                  className="rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-700 disabled:opacity-30"
                >
                  <ArrowDown className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => remove(entry.id)}
                  aria-label="Remove"
                  className="rounded-lg p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <span className="mb-2 block text-sm font-medium text-gray-700">Add your own</span>
        <div className="flex gap-2">
          <input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                addDraft();
              }
            }}
            placeholder="What time do you open?"
            disabled={atCapacity}
            className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-1 focus:ring-genie-primary disabled:bg-gray-50"
          />
          <Button type="button" variant="outline" onClick={addDraft} disabled={atCapacity || !draft.trim()}>
            <Plus className="mr-1.5 h-4 w-4" />
            Add
          </Button>
        </div>
      </div>

      <div>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
          <span className="text-sm font-medium text-gray-700">Suggestions</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void handleRecommend()}
            disabled={sourceCount === 0 || recommendQuestions.isPending}
            className="min-h-[44px] gap-1.5"
          >
            {recommendQuestions.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5 text-genie-primary" />
            )}
            {recommendQuestions.isPending ? "Reading your sources…" : "Recommend from my documents"}
          </Button>
        </div>

        {sourceCount === 0 ? (
          <p className="rounded-xl bg-gray-50 px-4 py-3 text-[13px] leading-snug text-gray-600">
            Add a source to this Genie's knowledge base first — then it can recommend the questions
            your customers are most likely to ask.
          </p>
        ) : suggestions.length > 0 ? (
          <>
            <div className="flex flex-wrap gap-2">
              {visibleSuggestions.map((suggestion) => (
                <button
                  key={suggestion.question}
                  type="button"
                  disabled={atCapacity}
                  onClick={() =>
                    add({
                      question: suggestion.question,
                      documentId: suggestion.documentId,
                      topic: suggestion.topic,
                    })
                  }
                  title={suggestion.question}
                  className="group flex min-h-[44px] max-w-full items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-left text-[13px] text-gray-700 transition-colors hover:border-genie-primary/50 hover:bg-genie-primary/5 disabled:opacity-40"
                >
                  <Plus className="h-3.5 w-3.5 shrink-0 text-genie-primary" />
                  <span className="truncate">{suggestion.question}</span>
                  {suggestion.recommended && (
                    <span className="shrink-0 rounded-full bg-genie-primary/10 px-1.5 py-0.5 text-[11px] font-semibold text-genie-primary">
                      Recommended
                    </span>
                  )}
                  {suggestion.occurrences !== null && (
                    <span className="shrink-0 rounded-full bg-genie-accent/15 px-1.5 py-0.5 text-[11px] font-semibold text-genie-accent-dark">
                      asked {suggestion.occurrences}×
                    </span>
                  )}
                </button>
              ))}
            </div>
            {suggestions.length > initialSuggestionCount && (
              <Button type="button" variant="ghost" className="mt-2 min-h-[44px]" onClick={() => setShowAllSuggestions((current) => !current)}>
                {showAllSuggestions ? "Show fewer suggestions" : `Show all ${suggestions.length} suggestions`}
              </Button>
            )}
            <p className="mt-2 text-xs text-gray-500">
              Recommended questions appear first. Select one to add it under “On the page”.
              Questions marked “asked” came from real conversations in the last 30 days. The rest are
              what your sources say they can answer — read a source in the knowledge base to add more.
            </p>
          </>
        ) : (
          <p className="rounded-xl bg-gray-50 px-4 py-3 text-[13px] leading-snug text-gray-600">
            {recommendedQuestions?.length
              ? "All recommended questions are already on your page."
              : "Nothing suggested yet — tap “Recommend from my documents” and your Genie will read its sources and propose the questions your customers are most likely to ask."}
          </p>
        )}
      </div>
    </div>
  );
};

export default FeaturedQuestionsPicker;
